<template>
  <StylistNav />
  <div class="container">
    
    <div class="manage-header" v-if="!new_blog && !edit_blog">
      <div class="welcome-section">
        <h1 class="page-head">Welcome back, {{ stylistData?.username || 'Stylist' }}!</h1>
        <p class="subtitle">Manage your publications.</p>
      </div>
      
      <div class="header-actions">
        <button class="cta-button" @click="createBlog">+ Create New Blog</button>
        <button v-if="blogs.length > 0" class="del-all-button" @click="deleteAll">⚠️ Bulk Clear All</button>
      </div>
    </div>

    <div v-if="!new_blog && !edit_blog" class="dashboard-view">
      
      <div v-if="isLoading" class="no-results">
        <p>Loading your publications...</p>
      </div>

      <div class="blog-list" v-else-if="blogs.length > 0">
        <div 
          class="blog-item" 
          v-for="blog in blogs" 
          :key="blog.id" 
          @click="openEditBlog(blog)"
        >
          <div class="blog-item-content">
            <h3 class="blog-title">{{ blog.title }}</h3>
            <p class="blog-preview" v-if="blog.date">Published: {{ formatDate(blog.date) }}</p>
            <p class="blog-preview draft" v-else>Draft</p>
          </div>
          
          <div class="service-footer">
            <div class="stats-group">
              <span class="blog-stats">👁️ {{ blog.views || 0 }}</span>
              <span class="blog-stats">❤️ {{ blog.likes || 0 }}</span>
            </div>
            <button class="del-button" @click.stop="deleteBlog(blog.id)">Delete</button>
          </div>
        </div>
      </div>

      <div v-else class="no-results">
        <p>You haven't written any blogs yet. Click "Create New Blog" to get started!</p>
      </div>
    </div>

    <NewBlog 
      v-if="new_blog"
      :stylist-data="stylistData"
      @submit-blog="submitBlogs"
      @cancel="closeForms"
    />
    
    <EditBlog 
      v-if="edit_blog"
      :blogData="selectedBlog"
      @submit-blog="submitBlogs"
      @cancel="closeForms"
    />
  </div>
</template>

<script>
import axiosInstance from '@/services/AxiosClient';
import EditBlog from '@/components/stylist/EditBlog.vue';
import NewBlog from '@/components/stylist/NewBlog.vue';
import StylistNav from '@/components/stylist/StylistNav.vue';

const AUTH_STORAGE_KEY = 'salon_auth';

export default {
    name: "ManageBlogs",
    components: {
        StylistNav,
        NewBlog,
        EditBlog,
    },
    data() {
        return {
            blogs: [],
            stylistData: null,
            selectedBlog: null,
            isLoading: true,
            error: null,
            new_blog: false,
            edit_blog: false,
        }
    },
    async mounted() {
      this.loadStylistData();
      if (this.stylistData?.id) {
          this.fetchBlogPosts(this.stylistData.id);
      } else {
          this.isLoading = false;
      }
    },
    methods: {
        // Updated to include time, matching the ViewBlog component
        formatDate(dateString) {
            if (!dateString) return "Unknown Date";
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString; 

            return new Intl.DateTimeFormat('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            }).format(date);
        },
        openEditBlog(blog) {
            this.selectedBlog = blog;
            this.new_blog = false;
            this.edit_blog = true;
        },
        createBlog() {
            this.selectedBlog = null;
            this.edit_blog = false;
            this.new_blog = true;
        },
        closeForms() {
            this.new_blog = false;
            this.edit_blog = false;
            this.selectedBlog = null;
        },
        async fetchBlogPosts(stylistId) {
            this.isLoading = true;
            try {
                const response = await axiosInstance.get(`/blog/get-stylist-blog/${stylistId}`);
                this.blogs = response.data;
            } catch(error) {
                console.error("Failed to load blog posts", error);
                this.error = "Could not load blogs at this time.";
            } finally {
                this.isLoading = false;
            }
        },
        loadStylistData() {
            const data = localStorage.getItem(AUTH_STORAGE_KEY);
            if (data) {
                const user = JSON.parse(data);
                this.stylistData = {
                    id: user.id,
                    username: user.username,
                    email: user.email,
                };
            }
        },
        async deleteBlog(blogID) {
            if (!confirm("Are you sure you want to permanently delete this blog post?")) return;
            try {
                await axiosInstance.delete(`/blog/delete-blog/${blogID}`);
                this.fetchBlogPosts(this.stylistData.id);
            } catch(error) {
                console.error("Error deleting blog post", error);
            }
        },
        deleteAll() {
            this.deleteAllBlogs(this.stylistData.id);
        },
        async deleteAllBlogs(stylistID) {
            if (!confirm("⚠️ Warning: This will delete ALL your published blog posts from our system. Proceed?")) return;
            try {
                await axiosInstance.delete(`/blog/delete-stylist-blogs/${stylistID}`);
            } catch(e) {
                console.error(e);
            } finally {
                this.fetchBlogPosts(stylistID);
            }
        },
        async submitBlogs(blogPayload) {
            if (blogPayload.state === "new") await this.storeNewBlog(blogPayload.blog, this.stylistData.id);
            if (blogPayload.state === "editted") await this.storeEdittedBlog(blogPayload.blog);
            this.closeForms();
        },
        async storeNewBlog(blog, stylistID) {
            try { 
                const formData = new FormData();
                formData.append('title', blog.title);
                formData.append('quillContent', blog.content); 
                formData.append('dateTime', blog.date || new Date().toISOString());
                formData.append('author', this.stylistData.username);

                await axiosInstance.post(`/blog/create/${stylistID}`, formData); 
                this.fetchBlogPosts(this.stylistData.id); 
            } catch(e) { console.error(e); }
        },
        async storeEdittedBlog(blog) {
            try { 
                const formData = new FormData();
                formData.append('title', blog.title);
                formData.append('quillContent', blog.content);
                formData.append('dateTime', blog.date);
                formData.append('author', blog.author);

                await axiosInstance.put(`/blog/update-blog/${blog.id}`, formData); 
                this.fetchBlogPosts(this.stylistData.id); 
            } catch(e) { console.error(e); }
        }
    }
}
</script>

<style scoped>
:root {
    --color-maroon: #5D0E1F;    
    --color-gold: #FBBF24;      
    --color-earth: #A0522D;     
    --color-dark: #1c1917;      
    --font-serif: 'Playfair Display', serif; 
    --font-sans: 'Inter', sans-serif;       
}

.container {
    max-width: 1000px;
    margin: 0 auto;
    padding: 3rem 1.5rem; 
    font-family: var(--font-sans);
}

.manage-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 3rem;
    flex-wrap: wrap;
    gap: 1.5rem;
    border-bottom: 1px solid #eaeaea;
    padding-bottom: 1.5rem;
}

.welcome-section {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.page-head {
    font-family: var(--font-serif);
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--color-maroon);
    margin: 0;
}

.subtitle {
    color: #666;
    font-size: 1.1rem;
    margin: 0;
}

.header-actions {
    display: flex;
    gap: 1rem;
    align-items: center;
}

/* --- HORIZONTAL 6:1 DASHBOARD LAYOUT --- */
.blog-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.blog-item {
    background-color: #fffaf0; 
    padding: 1.25rem 1.5rem; 
    border-radius: 0.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04); 
    border-left: 4px solid var(--color-gold); 
    transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
    cursor: pointer;
    
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 2rem;
}

.blog-item:hover {
    transform: translateX(4px); 
    background-color: #fff;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08); 
}

.blog-item-content {
    flex: 1; 
    min-width: 0; 
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.blog-title {
    font-family: var(--font-serif);
    font-size: 1.35rem;
    color: var(--color-dark);
    font-weight: 600;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.blog-preview {
    color: #666;
    font-size: 0.95rem;
    margin: 0;
}
.blog-preview.draft {
    color: var(--color-earth);
    font-weight: 600;
}

.service-footer {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 2rem;
    font-size: 0.9rem;
    color: var(--color-maroon);
    font-weight: 600;
    border-left: 1px solid #e2d8c3;
    padding-left: 1.5rem;
    white-space: nowrap;
}

.stats-group {
    display: flex;
    gap: 1.5rem;
}

.no-results {
    text-align: center;
    color: #666;
    font-size: 1.2rem;
    padding: 4rem;
    background: #f9f9f9;
    border-radius: 1rem;
    border: 1px dashed #ccc;
}

.cta-button {
    background-color: var(--color-maroon); 
    color: white;
    padding: 0.75rem 1.75rem; 
    border: none;
    border-radius: 8px; 
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease, transform 0.1s ease;
}
.cta-button:hover { background-color: #4a0c19; }
.cta-button:active { transform: scale(0.98); }

.del-all-button {
    background-color: transparent;
    color: #dc2626;
    padding: 0.74rem 1.5rem; 
    border: 1.5px solid #fca5a5;
    border-radius: 8px; 
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}
.del-all-button:hover {
    background-color: #fee2e2;
    border-color: #dc2626;
}

.del-button {
    background-color: #ef4444;
    color: white;
    padding: 0.5rem 1.2rem; 
    border: none;
    border-radius: 6px; 
    font-weight: 600;
    font-size: 0.85rem;
    cursor: pointer;
    transition: background-color 0.2s ease;
}
.del-button:hover { background-color: #b91c1c; }

@media (max-width: 768px) {
    .manage-header { flex-direction: column; align-items: flex-start; }
    .header-actions { width: 100%; justify-content: space-between; }
    
    .blog-item {
        flex-direction: column;
        align-items: flex-start;
        gap: 1rem;
    }
    .service-footer {
        flex-direction: row;
        justify-content: space-between;
        width: 100%;
        border-left: none;
        border-top: 1px solid #e2d8c3;
        padding-left: 0;
        padding-top: 1rem;
    }
    .blog-title {
        white-space: normal;
    }
}
</style>